import type { FC, ReactNode } from 'react'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react'
import {
    CircleAlert,
    CircleCheck,
    Info,
    X
} from 'lucide-react'
import { cn } from '@/lib/utils'

export type ToastTone = 'success' | 'error' | 'info'

export type ToastInput = {
    title: string
    description?: string
    tone?: ToastTone
    action?: {
        label: string
        onClick: () => void
    }
    duration?: number
}

type ToastItem = ToastInput & { id: number }

const ToastContext = createContext<(input: ToastInput) => void>(() => undefined)

const ICONS: Record<ToastTone, ReactNode> = {
    success: <CircleCheck className='size-5 text-emerald-600' aria-hidden='true' />,
    error: <CircleAlert className='size-5 text-red-600' aria-hidden='true' />,
    info: <Info className='size-5 text-primary' aria-hidden='true' />
}

const ToastCard: FC<{ item: ToastItem, onDismiss: () => void }> = ({ item, onDismiss }): ReactNode => {
    const tone = item.tone ?? 'success'

    return (
        <div
            role={tone === 'error' ? 'alert' : 'status'}
            className='pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-xl border border-border bg-card px-4 py-3 shadow-float animate-in fade-in slide-in-from-top-3 duration-200 lg:slide-in-from-bottom-3'
        >
            <span className='mt-0.5 shrink-0'>
                {ICONS[tone]}
            </span>

            <div className='min-w-0 flex-1'>
                <p className='text-sm leading-snug font-semibold text-foreground'>
                    {item.title}
                </p>

                {item.description && (
                    <p className='mt-0.5 text-[13px] leading-snug text-muted-foreground'>
                        {item.description}
                    </p>
                )}
            </div>

            {item.action && (
                <button
                    type='button'
                    onClick={() => {
                        item.action?.onClick()
                        onDismiss()
                    }}
                    className='-my-1 shrink-0 rounded-md px-2 py-1.5 text-sm font-semibold text-primary hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
                >
                    {item.action.label}
                </button>
            )}

            <button
                type='button'
                onClick={onDismiss}
                aria-label='Dismiss'
                className='-my-1 -mr-1.5 grid size-8 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-slate-100 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
            >
                <X className='size-4' aria-hidden='true' />
            </button>
        </div>
    )
}

export const ToastProvider: FC<{ children: ReactNode }> = ({ children }): ReactNode => {
    const [items, setItems] = useState<ToastItem[]>([])
    const counter = useRef(0)
    const timers = useRef(new Map<number, number>())

    const dismiss = useCallback((id: number) => {
        setItems(list => list.filter(item => item.id !== id))

        const timer = timers.current.get(id)
        if (timer) window.clearTimeout(timer)
        timers.current.delete(id)
    }, [])

    const show = useCallback((input: ToastInput) => {
        counter.current += 1
        const id = counter.current

        setItems(list => [...list.slice(-2), { ...input, id }])

        const timer = window.setTimeout(() => dismiss(id), input.duration ?? (input.action ? 6500 : 3800))
        timers.current.set(id, timer)
    }, [dismiss])

    useEffect(() => {
        const pending = timers.current
        return () => pending.forEach(timer => window.clearTimeout(timer))
    }, [])

    return (
        <ToastContext.Provider value={show}>
            {children}

            <div
                aria-live='polite'
                className={cn(
                    'pointer-events-none fixed inset-x-0 top-[calc(0.75rem+env(safe-area-inset-top))] z-[70] flex flex-col items-center gap-2 px-3 print:hidden',
                    'lg:top-auto lg:bottom-6 lg:items-end lg:px-6'
                )}
            >
                {items.map(item => (
                    <ToastCard key={item.id} item={item} onDismiss={() => dismiss(item.id)} />
                ))}
            </div>
        </ToastContext.Provider>
    )
}

export const useToast = () => useContext(ToastContext)
